/// <amd-module name="VSSUI/Components/RadioButton/RadioButton" />
import * as React from "react";
import { ObservableLike } from "VSS/Core/Observable";
import { FocusGroup, FocusGroupContext, IFocusGroupContext } from "VSSUI/FocusGroup";
import { FocusZone, FocusZoneContext, FocusZoneDirection, FocusZoneKeyStroke } from "VSSUI/FocusZone";
import { FormItem, FormItemContext } from "VSSUI/FormItem";
import { Observer } from "VSSUI/Observer";
import { Tooltip } from "VSSUI/TooltipEx";
import { css, getSafeId, isArrowKey, noop } from "VSSUI/Util";
import { getTabIndex, IFocusable } from "VSSUI/Utilities/Focus";
import { IRadioButtonGroup, IRadioButtonGroupProps, IRadioButtonProps, RadioButtonGroupDirection } from "./RadioButton.Props";

export interface IRadioButtonGroupContext {
    onSelect: (buttonId: string) => void;
    registerId: (safeId: string, id: string) => void;
    selectedButtonId?: string;
}

export const RadioButtonGroupContext = React.createContext<IRadioButtonGroupContext>({
    onSelect: noop,
    registerId: noop
});

export class RadioButtonGroup extends React.Component<IRadioButtonGroupProps> implements IRadioButtonGroup, IFocusable<{}> {
    public static defaultProps: IRadioButtonGroupProps = {
        direction: RadioButtonGroupDirection.Vertical,
        ariaInvalid: false
    };
    private focusGroupContext = React.createRef<FocusGroup>();
    private idMap: { [safeId: string]: string } = {};

    public render(): JSX.Element {
        const { className, defaultButtonId, direction, excludeFocusZone, id, selectedButtonId, text, ariaInvalid, ariaErrorMessageId } = this.props;
        const groupClassName = css(
            "bolt-radio-button-group",
            direction === RadioButtonGroupDirection.Vertical ? "flex-column" : "flex-row",
            this.props.groupClassName
        );

        return (
            <Observer selectedButtonId={selectedButtonId}>
                {(props: { selectedButtonId: string | undefined }) => (
                    <RadioButtonGroupContext.Provider
                        value={{
                            onSelect: this.onChange,
                            registerId: this.registerId,
                            selectedButtonId: props.selectedButtonId || this.getFirstButtonId()
                        }}
                    >
                        <FormItem className={css(className, "bolt-radio-button-group-container", excludeFocusZone && groupClassName)} label={text}>
                            {excludeFocusZone ? (
                                this.props.children
                            ) : (
                                <FormItemContext.Consumer>
                                    {formItemContext => (
                                        <FocusZone
                                            direction={
                                                direction === RadioButtonGroupDirection.Vertical
                                                    ? FocusZoneDirection.Vertical
                                                    : FocusZoneDirection.Horizontal
                                            }
                                            circularNavigation={true}
                                            focusGroupProps={{
                                                defaultElementId: defaultButtonId || props.selectedButtonId || this.getFirstButtonId(),
                                                ref: this.focusGroupContext
                                            }}
                                            postprocessKeyStroke={this.postProcessKeystroke}
                                        >
                                            <div
                                                aria-invalid={ariaInvalid}
                                                aria-errormessage={ariaErrorMessageId}
                                                aria-labelledby={getSafeId(formItemContext.ariaLabelledById)}
                                                className={groupClassName}
                                                role="radiogroup"
                                                id={getSafeId(id)}
                                            >
                                                {this.props.children}
                                            </div>
                                        </FocusZone>
                                    )}
                                </FormItemContext.Consumer>
                            )}
                        </FormItem>
                    </RadioButtonGroupContext.Provider>
                )}
            </Observer>
        );
    }

    public focus(): void {
        if (this.focusGroupContext.current) {
            this.focusGroupContext.current.focus();
        }
    }

    private onChange = (buttonId: string) => {
        if (this.props.onSelect) {
            this.props.onSelect(buttonId);
        }
    };

    private getFirstButtonId = (): string | undefined => {
        const children = React.Children.toArray(this.props.children);
        if (children.length) {
            const firstChild = children[0] as React.ReactElement<IRadioButtonProps>;
            return firstChild.props.id;
        }
        return undefined;
    };

    private postProcessKeystroke = (event: React.KeyboardEvent<HTMLElement>): FocusZoneKeyStroke => {
        if (document.activeElement && isArrowKey(event)) {
            const id = this.idMap[document.activeElement.id];
            if (id && id !== ObservableLike.getValue(this.props.selectedButtonId || "") && this.props.onSelect) {
                this.props.onSelect(id);
            }
        }

        return FocusZoneKeyStroke.IgnoreNone;
    };

    private registerId = (safeId: string, id: string) => {
        this.idMap[safeId] = id;
    };
}

export class RadioButton extends React.Component<IRadioButtonProps> implements IFocusable<{}> {
    private radioButtonInternal = React.createRef<RadioButtonInternal>();

    public render(): JSX.Element {
        return (
            <RadioButtonGroupContext.Consumer>
                {groupContext => (
                    <FocusGroupContext.Consumer>
                        {focusGroupContext => (
                            <FocusZoneContext.Consumer>
                                {zoneContext => {
                                    return (
                                        <RadioButtonInternal
                                            {...this.props}
                                            focusGroupContext={focusGroupContext}
                                            focuszoneId={zoneContext.focuszoneId}
                                            onFocus={focusGroupContext.onFocus}
                                            onSelect={groupContext.onSelect}
                                            registerId={groupContext.registerId}
                                            selectedButton={groupContext.selectedButtonId === this.props.id}
                                            ref={this.radioButtonInternal}
                                        />
                                    );
                                }}
                            </FocusZoneContext.Consumer>
                        )}
                    </FocusGroupContext.Consumer>
                )}
            </RadioButtonGroupContext.Consumer>
        );
    }

    public focus(): void {
        if (this.radioButtonInternal.current) {
            this.radioButtonInternal.current.focus();
        }
    }
}

interface IRadioButtonInternalProps extends IRadioButtonProps {
    focusGroupContext: IFocusGroupContext;
    focuszoneId?: string;
    registerId: (safeId: string, id: string) => void;
    selectedButton: boolean;
    onSelect: (buttonId: string) => void;
    onFocus: (buttonId: string) => void;
}

class RadioButtonInternal extends React.Component<IRadioButtonInternalProps> implements IFocusable<{}> {
    private radioButton = React.createRef<HTMLDivElement>();

    public render(): JSX.Element {
        const tabIndex = getTabIndex(this.props, this.props.focusGroupContext);
        const labelId = this.props.text && "rb-label-" + this.props.id;

        let radio = (
            <div
                aria-checked={this.props.selectedButton}
                aria-disabled={this.props.disabled}
                aria-labelledby={getSafeId(this.props.ariaLabelledBy || labelId)}
                aria-describedby={getSafeId(this.props.ariaDescribedBy)}
                className={css(
                    this.props.className,
                    "bolt-radio-button cursor-pointer",
                    this.props.disabled ? "disabled" : "enabled",
                    this.props.selectedButton && "checked",
                    (this.props.text || React.Children.count(this.props.children) > 0) && "labelled",
                    "bolt-focus-treatment"
                )}
                data-focuszone={!this.props.disabled && !this.props.excludeFocusZone ? this.props.focuszoneId : undefined}
                data-is-focusable={!this.props.disabled ? "true" : "false"}
                id={getSafeId(this.props.id)}
                key={this.props.id}
                onClick={this.onClick}
                onFocus={this.onFocus}
                ref={this.radioButton}
                role={this.props.role || "radio"}
                tabIndex={tabIndex}
            >
                <div className="bolt-radio-button-icon">
                    <div className="bolt-radio-button-bullet" />
                </div>
                {this.props.text && (
                    <div className="bolt-radio-button-label" id={getSafeId(labelId)}>
                        {this.props.text}
                    </div>
                )}
                {this.props.children}
            </div>
        );

        if (this.props.tooltipProps) {
            radio = <Tooltip {...this.props.tooltipProps}>{radio}</Tooltip>;
        }

        return radio;
    }

    public componentDidMount(): void {
        const { id, registerId } = this.props;
        registerId(getSafeId(id)!, id);
    }

    public focus(): void {
        if (this.radioButton.current) {
            this.radioButton.current.focus();
        }
    }

    private onClick = (event: React.MouseEvent<HTMLElement>) => {
        if (!event.defaultPrevented) {
            if (!this.props.disabled) {
                this.props.onSelect(this.props.id);
                event.preventDefault();
            }
        }
    };

    private onFocus = (event: React.FocusEvent<HTMLElement>) => {
        this.props.onFocus(this.props.id);
    };
}
